import { useMutation } from '@apollo/client';
import {
  ColumnDefinition,
  FlashMessageVariants,
  RegularTitle,
  RowDefinition,
  Table,
  useFlashMessage,
  useTableState
} from '@indigo-ag/component-module';
import { AgFinancingFile, AgFinancingMatricula, UpdateDecisionSheetByReferenceInputSchema } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import React, { useContext } from 'react';

import { UserContext } from 'core/components/GateWay/GateWay';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { CollateralUtil, DocumentCard, ICollateralAnalysisInfo, Permissions, createStylesHook, utils } from 'shared';
import { includeExcludeMatriculasInAnalysis, includeExcludeMatriculasInFormalization } from '../../queries';
import { MatriculaAnalysis } from './MatriculaAnalysis';
import { getFreeArea } from './utils';

const FLASH_MESSAGE_TIMEOUT = 6000;

export interface MatriculaAnalysisDetailsProps {
  analysisInfo?: ICollateralAnalysisInfo;
  includedInFormalization?: boolean;
  canEditMatriculaFormalization?: boolean;
}

const useStyles = createStylesHook({
  tableLayout: {
    '& div[role="table"]': {
      marginTop: '4px'
    }
  }
});

interface ITotalAreas {
  cultivableArea: string;
  percentageArea: string;
}

type ColumnId = 'cultivableArea' | 'percentageArea';

const columnHeadCells: ColumnDefinition<ITotalAreas, ColumnId>[] = [
  {
    id: 'cultivableArea',
    header: i18next.t('totalFreeArea'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${row.cultivableArea}`} />
  },
  {
    id: 'percentageArea',
    header: i18next.t('total60AreaFree'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${row.percentageArea}`} />
  }
];

const rowDefinition: RowDefinition<ITotalAreas, ColumnId> = {
  rowId: ({ row }) => `${row.cultivableArea}`
};

type MatriculasColumnId = 'file';
type MatriculasDefinitionContext = {
  includedInFormalization: boolean;
  readonly: boolean;
};

const matriculasColumnDefinition: ColumnDefinition<
  AgFinancingMatricula,
  MatriculasColumnId,
  MatriculasDefinitionContext
>[] = [
  {
    id: 'file',
    header: utils.text.toTitleCase(i18next.t('matriculas')),
    cell: ({ row, SubtitleCell }) => (
      <SubtitleCell label="" sublabel={<DocumentCard document={row.matricula as AgFinancingFile} />} />
    ),
    constraints: {
      min: { rem: 30 },
      max: { fr: 1 }
    }
  }
];

const matriculasRowDefinition: RowDefinition<AgFinancingMatricula, MatriculasColumnId, MatriculasDefinitionContext> = {
  rowId: ({ row }) => row.id,
  expandable: ({ row, definitionContext, ColumnSpan }) => {
    return (
      <ColumnSpan columnStart={1}>
        <MatriculaAnalysis
          matriculaData={row}
          readonly={!!definitionContext?.readonly}
          includedInFormalization={!!definitionContext?.includedInFormalization}
        />
      </ColumnSpan>
    );
  }
};

export const MatriculaAnalysisDetails: React.FunctionComponent<MatriculaAnalysisDetailsProps> = ({
  analysisInfo,
  includedInFormalization,
  canEditMatriculaFormalization
}) => {
  /**
   * Context
   */
  const { agFinance } = useContext(FinanceProcessContext);
  const { hasPermission } = useContext(UserContext);

  /**
   * styles
   */
  const classes = useStyles();

  /**
   * Mutations
   */
  const [includeExcludeMatriculasInAnalysisFn] = useMutation(includeExcludeMatriculasInAnalysis);
  const [includeExcludeMatriculasInFormalizationFn] = useMutation(includeExcludeMatriculasInFormalization);

  const { addFlashMessage, removeFlashMessage } = useFlashMessage();

  const matriculas = analysisInfo?.matriculas || [];
  const totals = getFreeArea(matriculas, includedInFormalization);

  const tableData: ITotalAreas[] = [
    {
      cultivableArea: utils.numberWithDots(totals.cultivableArea, 2),
      percentageArea: utils.numberWithDots(totals.percentageArea, 2)
    }
  ];

  const isInternal = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);

  const handleMatriculasAnalysis = (matriculasIds: AgFinancingMatricula['id'][], include: boolean) => {
    let msg: string;
    let variant = 'success' as FlashMessageVariants;
    if (include) {
      if (!matriculasIds.length) {
        msg = i18next.t('couldntIncludeMatriculaInAnalysis');
        variant = 'error';
      } else if (matriculasIds.length > 1) {
        msg = i18next.t('allMatriculasIncludedInAnalysis');
      } else {
        msg = i18next.t('matriculaIncludedInAnalysis');
      }
    } else {
      msg = i18next.t('matriculaExcludedFromAnalysis');
    }

    includeExcludeMatriculasInAnalysisFn({
      variables: {
        matriculasIds,
        include
      }
    })
      .then(() => {
        const flashId = addFlashMessage({
          content: utils.text.firstLetterUppercase(msg),
          title: '',
          variant: variant
        });
        // clear the error flash after 6s
        // success flashes do this automatically
        if (variant === 'error') {
          setTimeout(() => removeFlashMessage(flashId), FLASH_MESSAGE_TIMEOUT);
        }
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  const handleMatriculasFormalization = (
    matriculasIds: AgFinancingMatricula['id'][],
    include: boolean,
    decisionSheetInput: UpdateDecisionSheetByReferenceInputSchema,
    agFinancingId: string
  ) => {
    let msg: string;
    if (include) {
      if (!matriculasIds.length) {
        const flashId = addFlashMessage({
          content: utils.text.firstLetterUppercase(i18next.t('couldntIncludeMatriculaInFormalization')),
          title: '',
          variant: 'error'
        });
        setTimeout(() => removeFlashMessage(flashId), FLASH_MESSAGE_TIMEOUT);

        return;
      } else if (matriculasIds.length > 1) {
        msg = i18next.t('allMatriculasIncludedInFormalization');
      } else {
        msg = i18next.t('matriculaIncludedInFormalization');
      }
    } else {
      msg = i18next.t('matriculaExcludedFromFormalization');
    }

    includeExcludeMatriculasInFormalizationFn({
      variables: {
        matriculasIds,
        include,
        agFinancingId,
        decisionSheetInput
      }
    })
      .then(() => {
        addFlashMessage({
          content: utils.text.firstLetterUppercase(msg),
          title: '',
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  const tableState = useTableState<AgFinancingMatricula, MatriculasColumnId>(matriculas, {
    onTableAction: action => {
      switch (action.type) {
        case 'ROW_SELECTED':
          const matriculasIds = [
            ...matriculas
              .filter(
                m =>
                  [action.payload].includes(m.id) &&
                  m.details?.find(m => CollateralUtil.hasRegistrationNumber(m)) &&
                  m.details?.find(m => CollateralUtil.hasCultivableArea(m))
              )
              .map(m => m.id)
          ];

          if (includedInFormalization) {
            // Get actual cultivable area based on selection
            const selectedFiltered = matriculas.filter(
              m =>
                [action.payload].includes(m.id) ||
                (includedInFormalization ? m.includedInFormalization : m.includedInAnalysis)
            );
            const selectedArea = getFreeArea(selectedFiltered, includedInFormalization, true);

            agFinance &&
              handleMatriculasFormalization(
                matriculasIds,
                true,
                { collateralArea: selectedArea.cultivableArea },
                agFinance.id
              );
          } else {
            handleMatriculasAnalysis(matriculasIds, true);
          }
          break;
        case 'ROW_DESELECTED':
          const matriculasSelected = [
            ...CollateralUtil.filterMatriculasForDetails(matriculas, !!includedInFormalization)
          ];
          if (matriculasSelected.length > 1) {
            // Get actual cultivable area based on selection
            if (includedInFormalization) {
              const deselectedFiltered = matriculasSelected.filter(m => ![action.payload].includes(m.id));
              const deselectedArea = getFreeArea(deselectedFiltered, includedInFormalization);

              agFinance &&
                handleMatriculasFormalization(
                  [action.payload],
                  false,
                  { collateralArea: deselectedArea.cultivableArea },
                  agFinance.id
                );
            } else {
              handleMatriculasAnalysis([action.payload], false);
            }
          } else {
            const flashId = addFlashMessage({
              content: i18next.t('matriculasExcludedNotAllowed'),
              title: '',
              variant: 'error'
            });
            setTimeout(() => removeFlashMessage(flashId), FLASH_MESSAGE_TIMEOUT);
          }

          break;
        case 'ALL_ROWS_DESELECTED':
          const flashId = addFlashMessage({
            content: i18next.t('matriculasExcludedNotAllowed'),
            title: '',
            variant: 'error'
          });
          setTimeout(() => removeFlashMessage(flashId), FLASH_MESSAGE_TIMEOUT);
          break;
        case 'ROW_SELECTION_CHANGED': // ? ALL_ROWS_SELECTED does not get triggered
          const changedMatriculasIds = [
            ...matriculas
              .filter(
                m =>
                  m.details?.find(m => CollateralUtil.hasRegistrationNumber(m)) &&
                  m.details?.find(m => CollateralUtil.hasCultivableArea(m))
              )
              .map(m => m.id)
          ];
          const freeArea = getFreeArea(matriculas, includedInFormalization);

          includedInFormalization
            ? agFinance &&
              handleMatriculasFormalization(
                changedMatriculasIds,
                true,
                { collateralArea: freeArea.cultivableArea },
                agFinance.id
              )
            : handleMatriculasAnalysis(changedMatriculasIds, true);
          break;
        default:
          return;
      }
    }
  });

  const canEditMatricula = CollateralUtil.canEditMatricula(agFinance?.phase2?.collateral?.status);
  return (
    <>
      <div className="width-100">
        <RegularTitle style={{ marginLeft: 24 }}>{i18next.t('sumAddedArea')}</RegularTitle>
        <div style={{ padding: 10 }}>
          <Table
            label="Total Areas"
            rowDefinition={rowDefinition}
            columnDefinition={columnHeadCells}
            data={tableData}
          />
        </div>
        <div className={classes.tableLayout}>
          <Table
            {...tableState}
            tableStyle="inline"
            label="Matriculas"
            selectable={() => !isInternal && (canEditMatricula || !!canEditMatriculaFormalization)}
            hideHeaderCheckbox={!(canEditMatricula || canEditMatriculaFormalization)}
            selectedRows={{
              mode: 'pick',
              values: new Set(
                CollateralUtil.filterMatriculasForDetails(matriculas, !!includedInFormalization).map(m => m.id)
              )
            }}
            columnDefinition={matriculasColumnDefinition}
            rowDefinition={matriculasRowDefinition}
            definitionContext={{
              readonly: !(canEditMatricula || canEditMatriculaFormalization) || isInternal,
              includedInFormalization: !!includedInFormalization
            }}
          />
        </div>
      </div>
    </>
  );
};
